import { Prices } from '@mitsubishi/shared/data-access';

import { Alternative } from '../models/alternative.model';
import { CrossSellingPricesDto } from '../models/cross-selling.model';
import { AlternativeLooseAccessory } from '../models/loose-accessory.model';
import { Selection } from '../models/selection.model';

type SelectionLooseAccessories = Pick<Selection, 'looseAccessories'>;
type AlternativeLooseAccessories = Pick<Alternative, 'looseAccessories'>;

const SUMMED_PRICE_FIELDS = [
  'sellIn',
  'local',
  'priceList',
  'customer',
  'localWithCurrencyConversion',
  'sellInWithCurrencyConversion',
  'customerWithCurrencyConversion',
] as const;

/** The ids of a selection as the cross-selling endpoints take them. */
export type SelectionRef = { projectId: string; selectionId: string };

export function getSelectionRef(selection: Pick<Selection, 'details'> | null | undefined): SelectionRef | null {
  return selection?.details ? { projectId: selection.details.projectUuid, selectionId: selection.details.uuid } : null;
}

/**
 * The loose-accessory rows of an alternative, in `position` order: each link of the alternative joined with its
 * selection-level row. A payload without the maps (a backend without cross selling) has no rows.
 */
export function getAlternativeLooseAccessories(
  selection: SelectionLooseAccessories | null | undefined,
  alternative: AlternativeLooseAccessories | null | undefined
): AlternativeLooseAccessory[] {
  const accessories = selection?.looseAccessories ?? {};

  return Object.values(alternative?.looseAccessories ?? {})
    .filter(link => accessories[link.uuid] != null)
    .map(link => ({ accessory: accessories[link.uuid], link }))
    .sort((a, b) => a.link.position - b.link.position);
}

/** The quantity of each accessory code in an alternative, its rows of every source size added up: "Qty in quote". */
export function getAccessoryQuantitiesInAlternative(
  selection: SelectionLooseAccessories | null | undefined,
  alternative: AlternativeLooseAccessories | null | undefined
): ReadonlyMap<string, number> {
  const quantities = new Map<string, number>();
  for (const { accessory, link } of getAlternativeLooseAccessories(selection, alternative)) {
    quantities.set(accessory.accessoryCode, (quantities.get(accessory.accessoryCode) ?? 0) + link.quantity);
  }
  return quantities;
}

/**
 * The quantity of a row's accessory in the alternative if the row had `quantity` there, its rows of the other source
 * sizes included: what the minimum holds for.
 */
export function getAccessoryQuantityWithRow(
  selection: SelectionLooseAccessories | null | undefined,
  alternative: AlternativeLooseAccessories | null | undefined,
  { accessory, link }: AlternativeLooseAccessory,
  quantity: number
): number {
  const inAlternative = getAccessoryQuantitiesInAlternative(selection, alternative).get(accessory.accessoryCode) ?? 0;
  return inAlternative - link.quantity + quantity;
}

/** An alternative with neither units nor loose accessories: `unitCount` does not count the loose rows. */
export function isAlternativeEmpty(alternative: Pick<Alternative, 'unitCount' | 'looseAccessories'>): boolean {
  return alternative.unitCount === 0 && Object.keys(alternative.looseAccessories ?? {}).length === 0;
}

/** The ids of the selection's alternatives, Base first, that a row belongs to. */
export function getLinkedAlternativeIds(
  selection: Pick<Selection, 'base' | 'alternatives'> | null | undefined,
  isLinked: (alternative: Alternative) => boolean
): string[] {
  if (!selection) {
    return [];
  }
  return [selection.base, ...Object.values(selection.alternatives ?? {})]
    .filter(alternative => alternative != null && isLinked(alternative))
    .map(alternative => alternative.uuid);
}

/**
 * The total of some loose-accessory rows, field by field. A field no row has a value for (masked or missing in the
 * price list) stays null.
 */
export function sumLooseAccessoryTotals(items: readonly AlternativeLooseAccessory[]): Prices {
  const total: Prices = { sellIn: null, local: null, priceList: null, customer: null };
  for (const { link } of items) {
    for (const field of SUMMED_PRICE_FIELDS) {
      const value = link.totalsWithQuantity?.[field];
      if (value != null) {
        total[field] = (total[field] ?? 0) + value;
      }
    }
  }
  return total;
}

/** The generated price shape in the one the pricing components take: the currency codes are never null there. */
export function toPrices(prices: CrossSellingPricesDto): Prices {
  return {
    ...prices,
    currencyCode: prices.currencyCode ?? undefined,
    originalCurrencyCode: prices.originalCurrencyCode ?? undefined,
  };
}
